import { AlertTriangle, Building2 } from "lucide-react";
import type { ReactNode } from "react";

import type { RecipientClassification, Scope } from "@busright/data-contract";

import { ChartCard } from "@/components/ChartCard";
import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import {
  HealthPanel,
  InventoryPanel,
  OrdersPanel,
  RmaPanel,
  SupportPanel,
  TelemetryPanel,
} from "@/components/panels/ScopedPanels";
import { SkeletonLines } from "@/components/Skeleton";
import { useRecipientBundle } from "@/hooks/queries";
import { scopeDisplayName } from "@/lib/scope";

// =================================================================================================
// ScopedEntityPage — the reusable District/Recipient page template. It takes a `Scope`, fetches the
// RecipientScopedBundle ONCE, and composes the scoped panels (Health / Telemetry / Orders / Inventory
// / RMA / Support) around a human-name-first identity header (ids kept secondary, per the
// anti-"weird tag" rule). The Customers/Recipient surfaces render this; the future Device page can
// reuse the same panel set against a device-level bundle.
//
// Human naming: the header leads with `identity.displayName` (Linear source of truth); the raw E2
// name + machine ids sit in a secondary row. Classification (district / customer / one_off / pilot /
// internal) is shown so a one-off/internal recipient is never mislabeled as a customer.
//
// When the bundle endpoint isn't live the page degrades gracefully: panels show their pending stubs
// and the identity header falls back to the scope's own label — no fabricated entity.
// =================================================================================================

const CLASSIFICATION_LABEL: Record<RecipientClassification, string> = {
  district: "District",
  customer: "Customer",
  one_off: "One-off",
  pilot: "Pilot",
  internal: "BusRight internal",
};

export interface ScopedEntityPageProps {
  // The entity to scope to (level "district" or "account"), carrying its human label + parent.
  readonly scope: Scope;
}

export const ScopedEntityPage = ({ scope }: ScopedEntityPageProps): ReactNode => {
  const { data, isLoading, isError, error } = useRecipientBundle(scope);
  const bundle = data?.data;
  const meta = data?.meta;

  const displayName = bundle?.identity.displayName ?? scopeDisplayName(scope);

  if (isError) {
    return (
      <>
        <PageHeader title={displayName} meta={meta} />
        <ChartCard title="Recipient">
          <EmptyState
            title="Couldn't load this recipient"
            message={
              error instanceof Error
                ? `${error.message} — the recipient bundle endpoint may not be live yet.`
                : "The recipient bundle endpoint may not be live yet."
            }
            icon={AlertTriangle}
          />
        </ChartCard>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={displayName}
        description="Reusable scoped page — composed from health, telemetry, orders, inventory, RMA, and support panels."
        meta={meta}
      />

      <section className="entity-identity">
        <span className="entity-identity-icon" aria-hidden>
          <Building2 size={18} />
        </span>
        <div className="entity-identity-body">
          <div className="entity-identity-name">{displayName}</div>
          <div className="entity-identity-ids">
            {bundle ? (
              <>
                <span className="entity-chip">
                  {CLASSIFICATION_LABEL[bundle.identity.classification]}
                </span>
                {bundle.identity.state ? (
                  <span className="entity-chip">{bundle.identity.state}</span>
                ) : null}
                {bundle.identity.rawName !== displayName ? (
                  <span className="entity-id-raw">raw: {bundle.identity.rawName}</span>
                ) : null}
                {bundle.identity.districtId ? (
                  <span className="entity-id-raw br-tabular-nums">
                    district: {bundle.identity.districtId}
                  </span>
                ) : null}
              </>
            ) : (
              <span className="entity-id-raw">{scope.level} scope</span>
            )}
          </div>
        </div>
      </section>

      {isLoading ? (
        <ChartCard title="Loading recipient">
          <SkeletonLines count={4} />
        </ChartCard>
      ) : (
        <>
          <div className="grid cols-2">
            <HealthPanel scope={scope} health={bundle?.health ?? null} />
            <TelemetryPanel scope={scope} telemetry={bundle?.telemetry ?? null} />
          </div>
          <div className="grid cols-2" style={{ marginTop: 16 }}>
            <OrdersPanel scope={scope} orders={bundle?.orders ?? []} />
            <InventoryPanel scope={scope} />
          </div>
          <div className="grid cols-2" style={{ marginTop: 16 }}>
            <RmaPanel
              scope={scope}
              rmas={bundle?.rmas ?? { openCount: 0, agingCount: 0, resolvedCount: 0 }}
            />
            <SupportPanel support={bundle?.support ?? { byState: {}, total: 0 }} />
          </div>
        </>
      )}
    </>
  );
};
